import React, { useState, useEffect } from "react";

function LiabilityWaiverForm(props) {
  const [state, setState] = useState({
    checked: false,
    liability_waiver: false,
    liability_waiver_initials: "",
    liability_waiver_signature: "",
    signature_image: null
  });

  const [errors, setErrors] = useState({
    liability_waiver_signature: ""
  });

  // Define all required fields with their human-readable labels
  const requiredFields = [
    { field: 'liability_waiver', label: 'Digital Signature Checkbox' },
    { field: 'liability_waiver_signature', label: 'Signature' },
    ...(props.collect_initials ? [{ field: 'liability_waiver_initials', label: 'Initials' }] : [])
  ];

  const humanizeFieldLabel = (label) => {
    // First replace underscores with spaces
    let humanized = label.replace(/_/g, ' ');

    // Handle special cases for common abbreviations
    humanized = humanized
      .replace(/\bgpa\b/i, 'GPA')
      .replace(/\bsat\b/i, 'SAT')
      .replace(/\bact\b/i, 'ACT')
      .replace(/\burl\b/i, 'URL');
      
    // Capitalize first letter of each word
    return humanized
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getMissingFieldLabels = () => {
    return requiredFields.filter(({ field, label }) => {
      if (field === 'liability_waiver') {
        return !state.checked;
      }
      return !state[field] || state[field].toString().trim() === '';
    }).map(({ label }) => humanizeFieldLabel(label));
  };

  const [missingLabels, setMissingLabels] = useState(getMissingFieldLabels());

  // Update missing labels when form state changes
  useEffect(() => {
    setMissingLabels(getMissingFieldLabels());
  }, [state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleChecked = (e) => {
    setState(prevState => ({
      ...prevState,
      checked: e.target.checked,
      liability_waiver: e.target.checked
    }));
  };

  const validateFields = () => {
    const missing = getMissingFieldLabels();
    setMissingLabels(missing);

    let newErrors = { liability_waiver_signature: "" };
    if (!state.liability_waiver_signature || state.liability_waiver_signature.trim() === "") {
      newErrors.liability_waiver_signature = "A valid signature is required.";
    }
    setErrors(newErrors);

    return missing.length === 0;
  };

  const isValid = () => {
    return getMissingFieldLabels().length === 0;
  };

  const handleCamperSubmit = (e) => {
    e.preventDefault();

    if (!validateFields()) {
      return;
    }

    const newData = {
      liability_waiver: state.checked,
      liability_waiver_signature: state.liability_waiver_signature,
      liability_waiver_initials: state.liability_waiver_initials
    };

    props.saveValues(newData);
    props.nextStep();
  };

  const setTodaysDate = () => {
    let options = { year: "numeric", month: "long", day: "numeric" };
    return new Date().toLocaleDateString("en-US", options);
  };

  const setUpInitialsForm = () => {
    if (props.collect_initials) {
      return (
        <fieldset
          className="camper-info-row signature-info-box"
          aria-labelledby="initialsFieldsetLegend"
          role="group"
        >
          <legend id="initialsFieldsetLegend" className="lw-signature-label">
            Initials
          </legend>
          <div className="lw-signature-label">
            {`Initialed by ${props.signature_name} on ${setTodaysDate()}`}
          </div>
          <div className="camper-info-row lw-signature-container">
            <div className="signature-row">
              <i className="fas fa-pen-nib pen-polygon"></i>
              <label htmlFor="liability_waiver_initials" className="sr-only">
                Please enter your initials
              </label>
              <input
                type="text"
                className={`signature-input ${missingLabels.includes('Initials') ? 'missing-required' : ''}`}
                name="liability_waiver_initials"
                id="liability_waiver_initials"
                value={state.liability_waiver_initials}
                onChange={handleChange}
                aria-required="true"
                aria-invalid={missingLabels.includes('Initials')}
                aria-describedby={missingLabels.includes('Initials') ? "error-initials" : undefined}
              />
            </div>
            <div className="signature-row">
              <span className="enter-your-name">Please enter your initials</span>
            </div>
          </div>
        </fieldset>
      );
    }
    return null;
  };

  return (
    <form className="new-step-form" onSubmit={handleCamperSubmit} role="form">
      <div className="buck-test">
        <h1 className="form-heading" id="liabilityWaiverHeading">
          Event Waiver
        </h1>
      </div>

      <fieldset
        className="camper-info-row signature-info-box"
        aria-labelledby="waiverTextFieldsetLegend"
        role="group"
      >
        <legend id="waiverTextFieldsetLegend" className="sr-only">
          Waiver Text
        </legend>
        <div className="waiver-name">{props.waiverName}</div>
        <br/>
        <div className="waiver-text">{props.waiver}</div>
      </fieldset>

      {props.refundPolicy && (
        <fieldset
          className="camper-info-row signature-info-box"
          aria-labelledby="refundPolicyFieldsetLegend"
          role="group"
        >
          <legend id="refundPolicyFieldsetLegend" className="sr-only">
            Refund Policy
          </legend>
          <div className="waiver-name">Refund Policy</div>
          <br/>
          <div className="waiver-text">{props.refundPolicy}</div>
        </fieldset>
      )}


      <fieldset
        className={`camper-info-row signature-info-box ${missingLabels.includes('Digital Signature Checkbox') ? 'missing-required' : ''}`}
        style={{
          border: "2px solid rgb(99, 91, 255)",
          display: "flex",
          flexDirection: "row",
          alignItems: "center"
        }}
        aria-labelledby="digitalSignatureLegend"
        role="group"
      >
        <legend id="digitalSignatureLegend" className="sr-only">
          Digital Signature Checkbox
        </legend>
        <label
          htmlFor="liability_waiver"
          className="digi-sig"
          style={{ display: "inline-block", marginRight: "10px" }}
        >
          <span
            style={{
              fontSize: "1.2em",
              fontWeight: "bold",
              display: "block",
              marginBottom: "5px"
            }}
          >
            Digital Signature
          </span>
          I have read and agreed to the above waiver
        </label>
        <input
          type="checkbox"
          name="liability_waiver"
          id="liability_waiver"
          checked={state.checked}
          onChange={handleChecked}
          aria-required="true"
          aria-invalid={missingLabels.includes('Digital Signature Checkbox')}
          aria-describedby={missingLabels.includes('Digital Signature Checkbox') ? "error-checkbox" : undefined}
        />
      </fieldset>

      <div className="camper-info-row">{setUpInitialsForm()}</div>

      <fieldset
        className="camper-info-row signature-info-box"
        aria-labelledby="signatureFieldsetLegend"
        role="group"
      >
        <legend id="signatureFieldsetLegend" className="lw-signature-label sr-only">
          Signature Section
        </legend>
        <div className="lw-signature-label">
          {`Signed by ${props.signature_name} on ${setTodaysDate()}`}
        </div>
        <div className="camper-info-row lw-signature-container">
          <div className="signature-row">
            <i className="fas fa-pen-nib pen-polygon"></i>
            <label htmlFor="liability_waiver_signature" className="sr-only">
              Digital Signature
            </label>
            <input
              type="text"
              className={`signature-input ${missingLabels.includes('Signature') ? 'missing-required' : ''}`}
              name="liability_waiver_signature"
              id="liability_waiver_signature"
              value={state.liability_waiver_signature}
              onChange={handleChange}
              aria-required="true"
              aria-invalid={!!errors.liability_waiver_signature || missingLabels.includes('Signature')}
              aria-describedby={
                errors.liability_waiver_signature || missingLabels.includes('Signature')
                  ? "error-signature"
                  : undefined
              }
            />
          </div>
          <div className="signature-row">
            <span className="enter-your-name">
              {`Enter ${props.signature_name} to sign`}
            </span>
          </div>
        </div>
      </fieldset>

      {errors.liability_waiver_signature && (
        <div
          id="error-signature"
          role="alert"
          aria-live="assertive"
          style={{ color: "red", marginTop: "0.5rem" }}
        >
          {errors.liability_waiver_signature}
        </div>
      )}

      {missingLabels.length > 0 && (
        <div
          id="form-error-region"
          aria-live="polite"
          className="error-message-container"
        >
          <div className="error-content">
            <h4>
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
              Please fill in the following required fields:
            </h4>
            {missingLabels.join(", ")}
          </div>
        </div>
      )}

      <button
        type="submit"
        className="button button-primary create"
        disabled={!isValid()}
        style={props.buttonStyles}
        aria-labelledby="nextStepLabel"
        title={missingLabels.length > 0 ? `Missing required fields: ${missingLabels.join(", ")}` : ""}
      >
        <span id="nextStepLabel">Next Step</span>
      </button>
    </form>
  );
}

export default LiabilityWaiverForm;